import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/apiClient";

/** Request 1223 — Sticky Notes: each person's own notes that float over any screen. */
export type NoteColor = "lemon" | "rose" | "mint" | "sky" | "lilac" | "peach";
export interface StickyNote {
  id: string;
  text: string;
  color: NoteColor;
  x: number;
  y: number;
  width: number;
  height: number;
  z: number;
  pinned: boolean;
  /** request 1224 */
  minimized: boolean;
  createdAt: string;
  updatedAt: string;
}
export type NotePatch = Partial<Pick<StickyNote, "text" | "color" | "x" | "y" | "width" | "height" | "z" | "pinned" | "minimized">>;

const KEY = ["sticky-notes"];

export function useStickyNotes(enabled = true) {
  return useQuery({
    queryKey: KEY,
    queryFn: async () => (await api.get("/sticky-notes")).data.data as StickyNote[],
    enabled,
    staleTime: 60_000,
    refetchInterval: false,
  });
}

export function useAddStickyNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: NotePatch) => (await api.post("/sticky-notes", input)).data.data as StickyNote,
    onSuccess: (note) => qc.setQueryData<StickyNote[]>(KEY, (old) => [...(old ?? []), note]),
  });
}

export function useUpdateStickyNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...patch }: NotePatch & { id: string }) => (await api.patch(`/sticky-notes/${id}`, patch)).data.data as StickyNote,
    onMutate: ({ id, ...patch }) => qc.setQueryData<StickyNote[]>(KEY, (old) => (old ?? []).map((n) => (n.id === id ? { ...n, ...patch } : n))),
    // keep what's on screen (it may have moved on since), take the server's timestamps
    onSuccess: (saved) => qc.setQueryData<StickyNote[]>(KEY, (old) => (old ?? []).map((n) => (n.id === saved.id ? { ...n, updatedAt: saved.updatedAt } : n))),
  });
}

export function useDeleteStickyNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await api.delete(`/sticky-notes/${id}`)).data.data,
    onMutate: (id) => qc.setQueryData<StickyNote[]>(KEY, (old) => (old ?? []).filter((n) => n.id !== id)),
  });
}

/** request 1224 — minimise / restore every note at once */
export function useMinimizeAllStickyNotes() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (minimized: boolean) => (await api.post("/sticky-notes/minimize-all", { minimized })).data.data,
    onMutate: (minimized) => qc.setQueryData<StickyNote[]>(KEY, (old) => (old ?? []).map((n) => ({ ...n, minimized }))),
  });
}
